<template>
  <div class="view-all-panel">
    <div class="panel-header">
      <button
        class="close-btn"
        aria-label="Close panel"
        @click="$emit('close')"
      >
        <svg
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M18 6L6 18M6 6L18 18"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
        </svg>
      </button>
      <h2 class="panel-title">
        Revenue collected
      </h2>
    </div>
    <div class="panel-body">
      <div
        v-if="loading"
        class="panel-loading"
      >
        Loading...
      </div>
      <div
        v-else-if="error"
        class="panel-error"
      >
        Error: {{ error.message }}
      </div>
      <template v-else-if="accounts.length > 0">
        <table class="panel-table">
          <thead>
            <tr>
              <th>Account</th>
              <th class="text-right">
                Collected
              </th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="account in visibleAccounts"
              :key="account.id"
              :class="{ 'clickable': account.account_id }"
              @click="account.account_id && handleAccountClickGuarded(account)"
            >
              <td>
                <div class="user-cell">
                  <div class="user-image">
                    <img
                      v-if="account.avatar"
                      :src="account.avatar"
                      :alt="account.fullName"
                    >
                    <div
                      v-else
                      class="image-placeholder"
                    >
                      {{ getInitials(account.fullName) }}
                    </div>
                  </div>
                  <span class="user-name">{{ account.fullName }}</span>
                </div>
              </td>
              <td class="text-right">
                {{ account.revenueFormatted }}
              </td>
            </tr>
          </tbody>
        </table>
        <button
          v-if="hasMore"
          class="load-more-btn"
          @click="loadMore"
        >
          Load more
        </button>
      </template>
      <div
        v-else
        class="panel-empty"
      >
        No payments found
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, ref, onUnmounted } from 'vue'
import { useRevenueCollectedData } from '../composables/useWidgetData.js'
import { useTrialModeGuard } from '../composables/useTrialModeGuard.js'
import { DATE_RANGE_LABELS } from '../utils/dateRanges.js'

defineEmits(['close'])

const panelParams = new URLSearchParams(window.location.search)
const dateRangeParam = panelParams.get('dateRange')
const dateRange = dateRangeParam && DATE_RANGE_LABELS[dateRangeParam]
  ? dateRangeParam
  : 'last_30_days'
const excludeTax = panelParams.get('excludeTax') === '1'

const { data, loading, error, cleanup } = useRevenueCollectedData({
  dataKey: `revenueCollected_view_all_${dateRange}${excludeTax ? '_excl_tax' : ''}`,
  variables: computed(() => ({ dateRange, excludeTax })),
  priority: 1
})

const formatCurrency = (amount) => {
  const currencySymbol = data.value?.currency_symbol || '$'
  const locale = (data.value?.locale || 'en-US').replace(/_/g, '-')
  const currency = data.value?.currency || 'USD'
  if (amount === 0) return `${currencySymbol}0`
  try {
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount)
  } catch (e) {
    return `${currencySymbol}${amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
  }
}

const accounts = computed(() => {
  const raw = data.value?.accounts
  if (!Array.isArray(raw)) return []
  return raw.map((a) => ({
    id: a.account_id || a.name,
    account_id: a.account_id,
    fullName: a.name,
    avatar: a.avatar || null,
    revenueFormatted: formatCurrency(a.revenue)
  }))
})

const PAGE_SIZE = 20
const visibleCount = ref(PAGE_SIZE)
const visibleAccounts = computed(() => accounts.value.slice(0, visibleCount.value))
const hasMore = computed(() => visibleCount.value < accounts.value.length)
const loadMore = () => { visibleCount.value += PAGE_SIZE }

const getInitials = (name) => {
  if (!name) return '?'
  const words = name.split(' ')
  if (words.length >= 2) {
    return (words[0][0] + words[words.length - 1][0]).toUpperCase()
  }
  return name.charAt(0).toUpperCase()
}

const getUrlParams = () => {
  const urlParams = new URLSearchParams(window.location.search)
  let clientId = urlParams.get('client_id') || urlParams.get('clientId') || ''
  let canvasUUID = urlParams.get('canvasUUID') || urlParams.get('canvas_uuid') || urlParams.get('canvasUuid') || ''
  if (document.referrer) {
    try {
      const referrerParams = new URLSearchParams(new URL(document.referrer).search)
      if (!clientId) clientId = referrerParams.get('client_id') || referrerParams.get('clientId') || ''
      if (!canvasUUID) canvasUUID = referrerParams.get('canvasUUID') || referrerParams.get('canvas_uuid') || referrerParams.get('canvasUuid') || ''
    } catch (_e) { /* ignore */ }
  }
  return { client_id: clientId, canvasUUID }
}

const { guard } = useTrialModeGuard()
const handleAccountClickGuarded = guard((account) => {
  const accountId = account.account_id
  if (!accountId || (typeof accountId === 'string' && !/^\d+$/.test(accountId))) return
  const params = getUrlParams()
  const message = {
    command: 'ViewDetails',
    payload: {
      account_id: String(accountId),
      canvasUUID: params.canvasUUID || 'xxx'
    }
  }
  try {
    window.parent.postMessage(message, '*')
    if (window.top && window.top !== window.parent) {
      window.top.postMessage(message, '*')
    }
  } catch (e) {
    console.error('Failed to open account panel:', e)
  }
})

onUnmounted(() => {
  cleanup()
})
</script>

<style scoped>
.view-all-panel {
  width: 100%;
  height: 100%;
  background: white;
  display: flex;
  flex-direction: column;
}

.panel-header {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 24px;
  flex-shrink: 0;
}

.close-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border: none;
  background: transparent;
  color: rgba(0, 0, 0, 0.87);
  cursor: pointer;
  border-radius: 6px;
  transition: all 0.2s;
}

.close-btn:hover {
  background: #f3f4f6;
}

.panel-title {
  flex: 1;
  font-size: 24px;
  font-weight: 500;
  color: rgba(0, 0, 0, 0.87);
  margin: 0;
}

.panel-body {
  flex: 1;
  overflow-y: auto;
  padding: 24px;
}

.panel-loading,
.panel-empty {
  font-size: 14px;
  color: rgba(0, 0, 0, 0.6);
}

.panel-error {
  font-size: 14px;
  color: var(--color-red);
}

.panel-table {
  width: 100%;
  border-collapse: collapse;
}

.panel-table th {
  text-align: left;
  font-size: 12px;
  font-weight: 600;
  color: rgba(0, 0, 0, 0.87);
  padding: 10px 12px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.12);
}

.panel-table th.text-right {
  text-align: right;
  width: 120px;
}

.panel-table tbody tr {
  height: 48px;
}

.panel-table td {
  padding: 0 12px;
  font-size: 14px;
  color: rgba(0, 0, 0, 0.87);
  border-bottom: 1px solid rgba(0, 0, 0, 0.08);
}

.panel-table td.text-right {
  text-align: right;
  font-weight: 600;
}

.panel-table tbody tr:last-child td {
  border-bottom: none;
}

.panel-table tbody tr.clickable {
  cursor: pointer;
}

.panel-table tbody tr.clickable:hover {
  background-color: rgba(0, 0, 0, 0.04);
}

.user-cell {
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;
}

.user-image {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  overflow: hidden;
  flex-shrink: 0;
  background: #f0f0f0;
}

.user-image img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.image-placeholder {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  font-weight: 600;
  font-size: 14px;
}

.load-more-btn {
  width: 100%;
  padding: 12px;
  margin-top: 8px;
  border: 1px solid rgba(0, 0, 0, 0.12);
  border-radius: 8px;
  background: white;
  color: rgba(0, 0, 0, 0.87);
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: background 0.2s;
}

.load-more-btn:hover {
  background: #f3f4f6;
}

.user-name {
  font-size: 13px;
  color: rgba(0, 0, 0, 0.87);
  font-weight: 500;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  min-width: 0;
}
</style>
